import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2, Search } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';

type PlaylistRow = {
  id: string;
  attempt_id: string;
  display_order: number;
  is_active: boolean;
  added_at: string;
  attempt: {
    id: string;
    score: number | null;
    started_at: string;
    exercise: { id: string; title: string; difficulty: 'easy' | 'medium' | 'hard' };
    student: { display_name: string } | null;
  };
};

type AttemptOption = {
  id: string;
  score: number | null;
  started_at: string;
  step_count: number;
  exercise: { id: string; title: string; difficulty: 'easy' | 'medium' | 'hard' };
  student: { display_name: string } | null;
};

const DIFF_CLS = {
  easy: 'bg-xp-50 text-xp-600',
  medium: 'bg-level-50 text-level-600',
  hard: 'bg-accent-50 text-accent-600',
} as const;

function queryKey(objectiveTemplateId: string | null) {
  return ['admin', 'showcase-playlist', objectiveTemplateId ?? 'landing'] as const;
}

function usePlaylist(objectiveTemplateId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(objectiveTemplateId),
    enabled,
    queryFn: async (): Promise<PlaylistRow[]> => {
      let q = supabase
        .from('marketing_showcase_playlist')
        .select(
          `
          id, attempt_id, display_order, is_active, added_at,
          attempt:attempts!inner (
            id, score, started_at,
            exercise:exercises!inner ( id, title, difficulty ),
            student:profiles!attempts_student_id_fkey ( display_name:visible_name )
          )
        `,
        )
        .order('display_order', { ascending: true });

      q = objectiveTemplateId
        ? q.eq('objective_template_id', objectiveTemplateId)
        : q.is('objective_template_id', null);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as PlaylistRow[];
    },
  });
}

function useCandidateAttempts(query: string, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'showcase-candidates', query],
    enabled,
    queryFn: async (): Promise<AttemptOption[]> => {
      let q = supabase
        .from('attempts')
        .select(
          `
          id, score, started_at,
          exercise:exercises!inner ( id, title, difficulty, status ),
          student:profiles!attempts_student_id_fkey ( display_name:visible_name ),
          attempt_steps ( id )
        `,
        )
        .eq('status', 'completed')
        .eq('exercises.status', 'published')
        .order('started_at', { ascending: false })
        .limit(50);

      if (query.trim()) {
        q = q.ilike('exercises.title', `%${query.trim()}%`);
      }

      const { data, error } = await q;
      if (error) throw error;

      type Row = {
        id: string;
        score: number | null;
        started_at: string;
        attempt_steps: unknown[] | null;
        exercise: { id: string; title: string; difficulty: 'easy' | 'medium' | 'hard' };
        student: { display_name: string } | null;
      };
      return ((data ?? []) as unknown as Row[]).map((row) => ({
        id: row.id,
        score: row.score,
        started_at: row.started_at,
        step_count: Array.isArray(row.attempt_steps) ? row.attempt_steps.length : 0,
        exercise: row.exercise,
        student: row.student,
      }));
    },
  });
}

function useAddToPlaylist(objectiveTemplateId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      attemptId,
      nextOrder,
      userId,
    }: {
      attemptId: string;
      nextOrder: number;
      userId: string;
    }) => {
      const { error } = await supabase.from('marketing_showcase_playlist').insert({
        attempt_id: attemptId,
        display_order: nextOrder,
        added_by: userId,
        objective_template_id: objectiveTemplateId,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(objectiveTemplateId) }),
  });
}

function useUpdatePlaylistRow(objectiveTemplateId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: { is_active?: boolean; display_order?: number };
    }) => {
      const { error } = await supabase
        .from('marketing_showcase_playlist')
        .update(input.patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(objectiveTemplateId) }),
  });
}

function useDeletePlaylistRow(objectiveTemplateId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('marketing_showcase_playlist').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKey(objectiveTemplateId) }),
  });
}

export type ShowcasePlaylistEditorProps = {
  /**
   * `null` → playlist landing (Hero de la home).
   * Sinon → playlist du Hero de la landing /objectifs/[slug] correspondante.
   */
  objectiveTemplateId: string | null;
  userId: string;
  /**
   * Affiché en encart d'info au-dessus de la liste — utile pour rappeler le
   * fallback automatique sur la playlist landing côté objectif.
   */
  emptyHint?: string;
};

export function ShowcasePlaylistEditor({
  objectiveTemplateId,
  userId,
  emptyHint,
}: ShowcasePlaylistEditorProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data: playlist = [], isLoading } = usePlaylist(objectiveTemplateId, true);
  const { data: candidates = [], isLoading: candidatesLoading } = useCandidateAttempts(
    debouncedSearch,
    true,
  );
  const addToPlaylist = useAddToPlaylist(objectiveTemplateId);
  const updateRow = useUpdatePlaylistRow(objectiveTemplateId);
  const deleteRow = useDeletePlaylistRow(objectiveTemplateId);

  const inPlaylistIds = new Set(playlist.map((p) => p.attempt_id));
  const nextOrder =
    playlist.length === 0 ? 0 : Math.max(...playlist.map((p) => p.display_order)) + 1;

  function move(rowId: string, direction: -1 | 1) {
    const idx = playlist.findIndex((p) => p.id === rowId);
    if (idx === -1) return;
    const other = playlist[idx + direction];
    if (!other) return;
    const current = playlist[idx]!;
    updateRow.mutate({ id: current.id, patch: { display_order: other.display_order } });
    updateRow.mutate({ id: other.id, patch: { display_order: current.display_order } });
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">
          Dans la playlist ({playlist.length})
        </h2>
        {isLoading ? (
          <p className="text-ink-500 text-sm">Chargement…</p>
        ) : playlist.length === 0 ? (
          <p className="text-ink-500 text-sm">
            {emptyHint ?? 'Aucun élément. Ajoute un attempt depuis la liste ci-dessous.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {playlist.map((row, idx) => (
              <li
                key={row.id}
                className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center"
              >
                <span className="text-ink-400 w-8 shrink-0 text-center font-mono text-sm">
                  #{idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate font-semibold">
                    {row.attempt.exercise.title}
                  </p>
                  <p className="text-ink-500 text-xs">
                    par {row.attempt.student?.display_name ?? 'Élève inconnu'} · score{' '}
                    {row.attempt.score ?? '?'}%
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DIFF_CLS[row.attempt.exercise.difficulty]}`}
                    >
                      {row.attempt.exercise.difficulty}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="inline-flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={row.is_active}
                      onChange={(e) =>
                        updateRow.mutate({ id: row.id, patch: { is_active: e.target.checked } })
                      }
                    />
                    Actif
                  </label>
                  <button
                    type="button"
                    aria-label="Monter"
                    onClick={() => move(row.id, -1)}
                    disabled={idx === 0}
                    className="border-ink-200 hover:bg-ink-50 inline-flex h-9 w-9 items-center justify-center rounded-lg border disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Descendre"
                    onClick={() => move(row.id, 1)}
                    disabled={idx === playlist.length - 1}
                    className="border-ink-200 hover:bg-ink-50 inline-flex h-9 w-9 items-center justify-center rounded-lg border disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Retirer"
                    onClick={() => {
                      if (confirm('Retirer cet attempt de la playlist ?')) deleteRow.mutate(row.id);
                    }}
                    className="border-error-200 text-error-600 hover:bg-error-50 inline-flex h-9 w-9 items-center justify-center rounded-lg border"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="font-display text-ink-900 mb-3 text-lg font-bold">
          Attempts disponibles (terminés, exercices publiés)
        </h2>
        <div className="border-ink-200 mb-3 flex items-center gap-2 rounded-xl border bg-white px-3">
          <Search className="text-ink-400 h-4 w-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrer par titre d'exercice…"
            className="min-h-[2.75rem] flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        {candidatesLoading ? (
          <p className="text-ink-500 text-sm">Recherche…</p>
        ) : candidates.length === 0 ? (
          <p className="text-ink-500 text-sm">Aucun candidat.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {candidates.map((c) => {
              const already = inPlaylistIds.has(c.id);
              return (
                <li
                  key={c.id}
                  className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-ink-900 truncate font-semibold">{c.exercise.title}</p>
                    <p className="text-ink-500 text-xs">
                      par {c.student?.display_name ?? 'Élève inconnu'} · score {c.score ?? '?'}% ·{' '}
                      {c.step_count} étape
                      {c.step_count > 1 ? 's' : ''}
                      <span
                        className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DIFF_CLS[c.exercise.difficulty]}`}
                      >
                        {c.exercise.difficulty}
                      </span>
                    </p>
                  </div>
                  {already ? (
                    <span className="text-ink-400 text-xs italic">déjà dans la playlist</span>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() =>
                        addToPlaylist.mutate({
                          attemptId: c.id,
                          nextOrder,
                          userId,
                        })
                      }
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Ajouter
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
